import React from "react";
import styled, { css } from "styled-components";
import ArrowOutwardRoundedIcon from "@mui/icons-material/ArrowOutwardRounded";
import { useNavigate } from "react-router-dom";

import P from "./P";
import { getPartnerCode } from "../utils/partnerCode";

const CRM_LEADS_ENDPOINT = "https://crm.mangosoftwarehouse.com/api/public/leads";

const INITIAL_LEAD_FORM = {
    nome: "",
    email: "",
    telefone: "",
    empresa: "",
    mensagem: ""
};

const INITIAL_FIELD_ERRORS = {
    nome: "",
    email: "",
    telefone: "",
    empresa: "",
    mensagem: ""
};

const FIELD_LABELS = {
    nome: "Nome",
    email: "E-mail",
    telefone: "Telefone",
    empresa: "Empresa",
    mensagem: "Mensagem"
};

function getDigits(value) {
    return String(value || "").replace(/\D/g, "");
}

function formatPhone(value) {
    const digits = getDigits(value).slice(0, 11);

    if (!digits) {
        return "";
    }

    if (digits.length <= 2) {
        return `(${digits}`;
    }

    if (digits.length <= 6) {
        return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
    }

    if (digits.length <= 10) {
        return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
    }

    return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
}

function normalizeLeadForm(formData) {
    return {
        nome: formData.nome.trim(),
        email: formData.email.trim(),
        telefone: getDigits(formData.telefone),
        empresa: formData.empresa.trim(),
        mensagem: formData.mensagem.trim()
    };
}

function isValidEmail(email) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email);
}

function validateLeadForm(formData) {
    const normalizedForm = normalizeLeadForm(formData);
    const errors = { ...INITIAL_FIELD_ERRORS };

    if (normalizedForm.nome.length < 2) {
        errors.nome = "Informe seu nome completo.";
    }

    if (!normalizedForm.telefone) {
        errors.telefone = "Informe seu telefone com DDD.";
    } else if (![10, 11].includes(normalizedForm.telefone.length)) {
        errors.telefone = "Informe um telefone válido com 10 ou 11 dígitos.";
    }

    if (normalizedForm.email && !isValidEmail(normalizedForm.email)) {
        errors.email = "Informe um e-mail válido.";
    }

    if (normalizedForm.mensagem.length < 10) {
        errors.mensagem = "Descreva sua necessidade com pelo menos 10 caracteres.";
    }

    return {
        errors,
        isValid: !Object.values(errors).some(Boolean),
        normalizedForm
    };
}

function flattenApiMessages(value, keyPrefix) {
    if (!value) {
        return [];
    }

    if (typeof value === "string") {
        return [keyPrefix ? `${keyPrefix}: ${value}` : value];
    }

    if (Array.isArray(value)) {
        return value.flatMap((item) => flattenApiMessages(item, keyPrefix));
    }

    if (typeof value === "object") {
        return Object.entries(value).flatMap(([key, nestedValue]) => {
            const label = FIELD_LABELS[key] || key;
            return flattenApiMessages(nestedValue, label);
        });
    }

    return [];
}

function getApiFieldErrors(payload) {
    if (!payload || typeof payload !== "object" || !payload.errors || typeof payload.errors !== "object" || Array.isArray(payload.errors)) {
        return {};
    }

    return Object.entries(payload.errors).reduce((fieldErrors, [field, value]) => {
        if (!Object.prototype.hasOwnProperty.call(INITIAL_FIELD_ERRORS, field)) {
            return fieldErrors;
        }

        return {
            ...fieldErrors,
            [field]: flattenApiMessages(value).join(" ")
        };
    }, {});
}

function getApiErrorMessage(payload) {
    if (!payload) {
        return "Não foi possível enviar o formulário.";
    }

    if (typeof payload === "string") {
        return payload;
    }

    const messages = [
        ...flattenApiMessages(payload.message),
        ...flattenApiMessages(payload.error),
        ...flattenApiMessages(payload.detail),
        ...flattenApiMessages(payload.errors)
    ].filter(Boolean);

    return messages.length ? messages.join(" ") : "Não foi possível enviar o formulário.";
}

async function readResponsePayload(response) {
    const contentType = response.headers.get("content-type") || "";

    if (contentType.includes("application/json")) {
        try {
            return await response.json();
        } catch (error) {
            return null;
        }
    }

    if (typeof response.text !== "function") {
        return null;
    }

    const text = await response.text();
    return text || null;
}

async function submitLead(formData) {
    const partnerCode = getPartnerCode();

    const endpoint = partnerCode
        ? `${CRM_LEADS_ENDPOINT}?partner_code=${encodeURIComponent(partnerCode)}`
        : CRM_LEADS_ENDPOINT;

    const response = await fetch(endpoint, {
        method: "POST",
        headers: {
            "Content-Type": "application/json"
        },
        body: JSON.stringify({
            nome: formData.nome.trim(),
            email: formData.email.trim() || null,
            telefone: getDigits(formData.telefone) || null,
            empresa: formData.empresa.trim() || null,
            mensagem: formData.mensagem.trim() || null
        })
    });

    const payload = await readResponsePayload(response);

    if (!response.ok) {
        const error = new Error(getApiErrorMessage(payload));
        error.fieldErrors = getApiFieldErrors(payload);
        throw error;
    }

    return payload;
}

const Form = styled.form`
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding: 28px;
    border-radius: 30px;
    background: rgba(255, 255, 255, 0.82);
    border: 1px solid rgba(126, 94, 221, 0.12);
    box-shadow: var(--shadow-soft);
    backdrop-filter: blur(16px);

    @media only screen and (max-width: 640px) {
        gap: 14px;
        padding: 20px;
        border-radius: 20px;
    }
`

const FormHeader = styled.div`
    display: grid;
    gap: 8px;
`

const FormTitle = styled.h3`
    margin: 0;
    font-family: var(--font-display);
    font-size: clamp(1.55rem, 2.4vw, 2.25rem);
    line-height: 1;
    color: var(--ink);
`

const FormGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 14px;

    @media only screen and (max-width: 640px) {
        grid-template-columns: 1fr;
    }
`

const FormField = styled.label`
    display: grid;
    gap: 8px;
    color: var(--ink);
    font-size: 0.92rem;
    font-weight: 700;

    ${({ $full }) => $full && css`
        grid-column: 1 / -1;
    `}
`

const fieldControl = css`
    width: 100%;
    border: 1px solid rgba(126, 94, 221, 0.12);
    border-radius: 18px;
    background: rgba(255, 255, 255, 0.78);
    color: var(--ink);
    outline: none;
    transition: border-color 0.2s ease, box-shadow 0.2s ease, background-color 0.2s ease;

    &::placeholder {
        color: rgba(108, 99, 133, 0.62);
    }

    &:focus {
        border-color: rgba(255, 90, 191, 0.36);
        background: rgba(255, 255, 255, 0.96);
        box-shadow: 0 0 0 4px rgba(255, 90, 191, 0.08);
    }

    ${({ $error }) => $error && css`
        border-color: rgba(255, 90, 112, 0.48);
        background: rgba(255, 255, 255, 0.96);
        box-shadow: 0 0 0 4px rgba(255, 90, 112, 0.08);
    `}

    @media only screen and (max-width: 640px) {
        border-radius: 14px;
    }
`

const FormInput = styled.input`
    ${fieldControl}
    min-height: 52px;
    padding: 0 16px;
`

const FormTextarea = styled.textarea`
    ${fieldControl}
    min-height: 132px;
    padding: 14px 16px;
    line-height: 1.5;
    resize: vertical;
`

const SubmitButton = styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    width: fit-content;
    min-height: 54px;
    padding: 0 24px;
    border: 1px solid rgba(255, 255, 255, 0.82);
    border-radius: 999px;
    background-color: var(--brand);
    background-image: linear-gradient(135deg, var(--brand), var(--brand-2));
    color: #ffffff;
    font-weight: 700;
    cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};
    opacity: ${({ disabled }) => (disabled ? 0.72 : 1)};
    box-shadow: 0 18px 32px rgba(117, 83, 255, 0.18);
    transition: transform 0.25s ease, box-shadow 0.25s ease, opacity 0.25s ease;

    ${({ disabled }) => !disabled && css`
        &:hover {
            transform: translateY(-2px);
            box-shadow: 0 24px 42px rgba(117, 83, 255, 0.28);
        }
    `}

    @media only screen and (max-width: 600px) {
        width: 100%;
        min-height: 50px;
    }
`

const FormMessage = styled.p`
    margin: 0;
    padding: 12px 14px;
    border-radius: 18px;
    font-size: 0.94rem;
    font-weight: 700;
    color: ${({ $status }) => ($status === "success" ? "#1f6b45" : "#8b263d")};
    background: ${({ $status }) => ($status === "success" ? "rgba(58, 184, 119, 0.12)" : "rgba(255, 90, 112, 0.12)")};
    border: 1px solid ${({ $status }) => ($status === "success" ? "rgba(58, 184, 119, 0.18)" : "rgba(255, 90, 112, 0.18)")};
`

const FieldError = styled.span`
    color: #8b263d;
    font-size: 0.84rem;
    font-weight: 700;
    line-height: 1.35;
`

function LeadCaptureForm ({
    title = "Receba um retorno da Mango",
    description = "Preencha os dados principais para a Mango retornar com mais contexto.",
    submitLabel = "Enviar mensagem",
    loadingLabel = "Enviando...",
    successMessage = "Recebemos seu contato. A Mango retorna em breve.",
    successRedirectPath = "/obrigado",
    ariaLabel = "Formulário de contato"
}) {
    const navigate = useNavigate();
    const [leadForm, setLeadForm] = React.useState(INITIAL_LEAD_FORM);
    const [fieldErrors, setFieldErrors] = React.useState(INITIAL_FIELD_ERRORS);
    const [leadStatus, setLeadStatus] = React.useState("idle");
    const [leadMessage, setLeadMessage] = React.useState("");

    const handleLeadChange = (event) => {
        const { name, value } = event.target;
        const nextValue = name === "telefone" ? formatPhone(value) : value;

        setLeadForm((currentForm) => ({
            ...currentForm,
            [name]: nextValue
        }));

        setFieldErrors((currentErrors) => ({
            ...currentErrors,
            [name]: ""
        }));

        if (leadStatus !== "loading") {
            setLeadStatus("idle");
            setLeadMessage("");
        }
    };

    const handleLeadSubmit = async (event) => {
        event.preventDefault();

        if (leadStatus === "loading") {
            return;
        }

        const validation = validateLeadForm(leadForm);

        if (!validation.isValid) {
            setFieldErrors(validation.errors);
            setLeadStatus("error");
            setLeadMessage("Revise os campos destacados e tente novamente.");
            return;
        }

        setFieldErrors(INITIAL_FIELD_ERRORS);
        setLeadStatus("loading");
        setLeadMessage("");

        try {
            await submitLead(validation.normalizedForm);

            setLeadForm(INITIAL_LEAD_FORM);
            setFieldErrors(INITIAL_FIELD_ERRORS);
            setLeadStatus("success");
            setLeadMessage(successMessage);

            if (successRedirectPath) {
                navigate(successRedirectPath);
            }
        } catch (error) {
            setFieldErrors({
                ...INITIAL_FIELD_ERRORS,
                ...(error.fieldErrors || {})
            });
            setLeadStatus("error");
            setLeadMessage(error.message || "Não conseguimos enviar agora. Tente novamente em instantes ou chame pelo WhatsApp.");
        }
    };

    const leadIsLoading = leadStatus === "loading";

    return (
        <Form noValidate onSubmit={handleLeadSubmit} aria-label={ariaLabel} aria-busy={leadIsLoading}>
            <FormHeader>
                <FormTitle>{title}</FormTitle>
                {description && <P>{description}</P>}
            </FormHeader>

            <FormGrid>
                <FormField>
                    Nome
                    <FormInput
                        name="nome"
                        value={leadForm.nome}
                        onChange={handleLeadChange}
                        placeholder="Seu nome"
                        autoComplete="name"
                        aria-invalid={Boolean(fieldErrors.nome)}
                        aria-describedby={fieldErrors.nome ? "nome-error" : undefined}
                        $error={Boolean(fieldErrors.nome)}
                        required
                    />
                    {fieldErrors.nome && (
                        <FieldError id="nome-error">{fieldErrors.nome}</FieldError>
                    )}
                </FormField>

                <FormField>
                    Telefone
                    <FormInput
                        type="tel"
                        name="telefone"
                        value={leadForm.telefone}
                        onChange={handleLeadChange}
                        placeholder="(85) 99999-9999"
                        autoComplete="tel"
                        inputMode="tel"
                        maxLength={15}
                        aria-invalid={Boolean(fieldErrors.telefone)}
                        aria-describedby={fieldErrors.telefone ? "telefone-error" : undefined}
                        $error={Boolean(fieldErrors.telefone)}
                        required
                    />
                    {fieldErrors.telefone && (
                        <FieldError id="telefone-error">{fieldErrors.telefone}</FieldError>
                    )}
                </FormField>

                <FormField>
                    E-mail
                    <FormInput
                        type="email"
                        name="email"
                        value={leadForm.email}
                        onChange={handleLeadChange}
                        placeholder="email@exemplo.com"
                        autoComplete="email"
                        aria-invalid={Boolean(fieldErrors.email)}
                        aria-describedby={fieldErrors.email ? "email-error" : undefined}
                        $error={Boolean(fieldErrors.email)}
                    />
                    {fieldErrors.email && (
                        <FieldError id="email-error">{fieldErrors.email}</FieldError>
                    )}
                </FormField>

                <FormField>
                    Empresa
                    <FormInput
                        name="empresa"
                        value={leadForm.empresa}
                        onChange={handleLeadChange}
                        placeholder="Nome da empresa"
                        autoComplete="organization"
                        aria-invalid={Boolean(fieldErrors.empresa)}
                        aria-describedby={fieldErrors.empresa ? "empresa-error" : undefined}
                        $error={Boolean(fieldErrors.empresa)}
                    />
                    {fieldErrors.empresa && (
                        <FieldError id="empresa-error">{fieldErrors.empresa}</FieldError>
                    )}
                </FormField>

                <FormField $full>
                    Mensagem
                    <FormTextarea
                        name="mensagem"
                        value={leadForm.mensagem}
                        onChange={handleLeadChange}
                        placeholder="Conte sobre a ideia, o gargalo ou a necessidade"
                        aria-invalid={Boolean(fieldErrors.mensagem)}
                        aria-describedby={fieldErrors.mensagem ? "mensagem-error" : undefined}
                        $error={Boolean(fieldErrors.mensagem)}
                        required
                    />
                    {fieldErrors.mensagem && (
                        <FieldError id="mensagem-error">{fieldErrors.mensagem}</FieldError>
                    )}
                </FormField>
            </FormGrid>

            <SubmitButton type="submit" disabled={leadIsLoading}>
                {leadIsLoading ? loadingLabel : submitLabel}
                <ArrowOutwardRoundedIcon fontSize="small" />
            </SubmitButton>

            {leadMessage && (
                <FormMessage $status={leadStatus} role="status" aria-live="polite">
                    {leadMessage}
                </FormMessage>
            )}
        </Form>
    )
}

export {
    CRM_LEADS_ENDPOINT,
    LeadCaptureForm
}
export default LeadCaptureForm
